/**
 * Custom Geometry CFD Page
 * Upload STL files and run CFD simulations on custom geometries.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/authService';
import simulationService from '../../services/simulationService';
import WingViewer3D from '../../components/dafoam/WingViewer3D';
import { useTranslation } from 'react-i18next';

const CustomGeometryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [inletVelocity, setInletVelocity] = useState(10.0);

  // Create blob URL for 3D preview
  const previewUrl = useMemo(() => {
    if (selectedFile) {
      return URL.createObjectURL(selectedFile);
    }
    return null;
  }, [selectedFile]);

  // Cleanup blob URL when file changes or component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [canLaunch, setCanLaunch] = useState(null);
  const [permissionReason, setPermissionReason] = useState(null);

  // Check launch permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      const result = await simulationService.canLaunchSimulations();
      setCanLaunch(result.can_launch);
      setPermissionReason(result.reason);
    };
    checkPermissions();
  }, []);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.stl')) {
      setError(t('geometrie.stlUniquement'));
      return;
    }

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      setError(t('geometrie.tropVolumineux'));
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file) {
      // Simulate file input change
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
      handleFileSelect({ target: { files: [file] } });
    }
  };

  // Upload file and launch simulation
  const handleLaunch = async () => {
    if (!selectedFile || !canLaunch) return;

    setIsUploading(true);
    setIsLaunching(false);
    setError(null);

    try {
      // Step 1: Get presigned POST fields
      const uploadUrlResponse = await api.post('/api/simulations/upload-url', {
        filename: selectedFile.name,
      });

      const { upload_url, upload_fields, s3_key } = uploadUrlResponse.data;

      // Step 2: Upload file directly to S3 using presigned POST
      setUploadProgress(0);
      await new Promise((resolve, reject) => {
        const formData = new FormData();

        // Add all presigned fields first (order matters for S3)
        Object.entries(upload_fields).forEach(([key, value]) => {
          formData.append(key, value);
        });

        // Add the file last (required by S3)
        formData.append('file', selectedFile);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', upload_url, true);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(progress);
          }
        };

        xhr.onload = () => {
          if (xhr.status === 204 || xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error(t('geometrie.echecEnvoi')));
        xhr.send(formData);
      });

      // Step 3: Launch simulation
      setIsUploading(false);
      setIsLaunching(true);

      const response = await simulationService.createSimulation({
        workflow_mode: 'custom_geometry',
        analysis_mode: 'cfd',
        stl_s3_key: s3_key,
        inlet_velocity: parseFloat(inletVelocity)
      });

      // Navigate to the detail page
      navigate(`/app/simulations/${response.simulation_id}`);

    } catch (err) {
      setError(err.response?.data?.message || err.message || t('geometrie.echecLancement'));
      setIsUploading(false);
      setIsLaunching(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isProcessing = isUploading || isLaunching;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-dark-100">Custom Geometry CFD</h1>
        <p className="text-dark-400 mt-1">
          Upload your STL geometry file and run CFD simulations
        </p>
      </div>

      {/* Permission Warning Banner */}
      {canLaunch === false && (
        <div className="bg-warning-500/10 border border-warning-500/30 rounded-lg p-4 flex items-start gap-3">
          <span className="text-2xl">🔒</span>
          <div>
            <p className="text-warning-400 font-medium">{t('geometrie.lancementDesactive')}</p>
            <p className="text-warning-500 text-sm mt-1">
              {permissionReason || "You don't have permission to launch simulations."}
            </p>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400">{error}</p>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* File Upload Card */}
        <div className="card-dark p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">
            1. Upload STL Geometry
          </h2>

          {/* Dropzone */}
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              selectedFile
                ? 'border-primary-500 bg-primary-500/5'
                : 'border-dark-600 hover:border-dark-500 hover:bg-dark-800/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".stl"
              onChange={handleFileSelect}
              className="hidden"
            />

            {selectedFile ? (
              <div className="space-y-2">
                <div className="w-16 h-16 bg-primary-500/20 rounded-full mx-auto flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-dark-100 font-medium">{selectedFile.name}</p>
                <p className="text-dark-400 text-sm">{formatFileSize(selectedFile.size)}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="text-primary-400 text-sm hover:text-primary-300"
                >
                  Change file
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="w-16 h-16 bg-dark-700 rounded-full mx-auto flex items-center justify-center">
                  <svg className="w-8 h-8 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-dark-100">
                  <span className="text-primary-400">{t('geometrie.cliquerPourEnvoyer')}</span> or drag and drop
                </p>
                <p className="text-dark-500 text-sm">{t('geometrie.stlSeulement')}</p>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-dark-400 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-dark-700 rounded-full h-2">
                <div
                  className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Parameters Card */}
        <div className="card-dark p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">
            2. Simulation Parameters
          </h2>

          {/* Inlet Velocity */}
          <div className="space-y-4">
            <div>
              <label className="block text-dark-300 text-sm font-medium mb-2">
                Inlet Velocity (m/s)
              </label>
              <input
                type="number"
                value={inletVelocity}
                onChange={(e) => setInletVelocity(e.target.value)}
                min="0.1"
                max="500"
                step="0.1"
                disabled={isProcessing}
                className="w-full bg-dark-900 border border-dark-600 rounded-lg px-4 py-3 text-dark-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:opacity-50"
              />
              <p className="text-dark-500 text-xs mt-1">
                Flow velocity at the inlet boundary (0.1 - 500 m/s)
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-dark-900 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-dark-700 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-dark-300 font-medium">{t('geometrie.detailsSimulation')}</p>
                  <p className="text-dark-500 text-sm">{t('geometrie.sousTitre')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-dark-700">
                <div>
                  <p className="text-dark-500 text-xs">Duration</p>
                  <p className="text-dark-300">~1-2 hours</p>
                </div>
                <div>
                  <p className="text-dark-500 text-xs">{t('geometrie.coutEstime')}</p>
                  <p className="text-dark-300">$0.15 - $0.40</p>
                </div>
                <div>
                  <p className="text-dark-500 text-xs">Solver</p>
                  <p className="text-dark-300">DASimpleFoam</p>
                </div>
                <div>
                  <p className="text-dark-500 text-xs">Mesh</p>
                  <p className="text-dark-300">snappyHexMesh</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3D Preview */}
      {selectedFile && previewUrl && (
        <div className="card-dark p-6">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">
            3D Preview
          </h2>
          <WingViewer3D
            stlUrl={previewUrl}
            title={selectedFile.name}
          />
        </div>
      )}

      {/* Launch Button */}
      <div className="card-dark p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-dark-300">
              {selectedFile ? (
                <>{t('geometrie.pretALancer')} <span className="text-primary-400 font-medium">{selectedFile.name}</span></>
              ) : (
                t('geometrie.choisirFichier')
              )}
            </p>
            {selectedFile && (
              <p className="text-dark-500 text-sm mt-1">
                Inlet velocity: {inletVelocity} m/s
              </p>
            )}
          </div>
          <button
            onClick={handleLaunch}
            disabled={!selectedFile || isProcessing || canLaunch === false || canLaunch === null}
            className="btn-primary px-8 py-3 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {canLaunch === null ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Checking permissions...
              </>
            ) : isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Uploading ({uploadProgress}%)...
              </>
            ) : isLaunching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Launching...
              </>
            ) : canLaunch === false ? (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Launch Disabled
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Launch Simulation
              </>
            )}
          </button>
        </div>
      </div>

      {/* How It Works */}
      <div className="card-dark p-6">
        <h2 className="text-lg font-semibold text-dark-100 mb-4">{t('geometrie.commentCaMarche')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { step: '1', title: 'Upload', desc: 'Upload your 3D geometry as an STL file' },
            { step: '2', title: 'Mesh', desc: 'Automatic mesh generation with snappyHexMesh' },
            { step: '3', title: 'Simulate', desc: 'Run the high-fidelity CFD simulation' },
            { step: '4', title: 'Results', desc: 'Get drag (CD) and lift (CL) coefficients' },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-10 h-10 bg-primary-500/20 rounded-full mx-auto flex items-center justify-center mb-2">
                <span className="text-primary-400 font-bold">{item.step}</span>
              </div>
              <h3 className="text-dark-200 font-medium">{item.title}</h3>
              <p className="text-dark-500 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomGeometryPage;
